import {colors} from "@c10h/colors";
import {Testimonial} from "@components/TestimonialSlider";
import {Trans, useTranslation} from "@i18n/client";
import React from "react";
import {ImageItem} from "src/components/_common/ImageItemList";
import {QA, SectionDivider} from "src/components/v5/Faq/types";
import {BtnProps} from "src/components/v5/V5Comp";
import {useFaqIndex} from "src/hooks/useFaqIndex";
import doctorAndPatientIllustration from "public/static/img/v5/illustrations/doctor-and-patient.jpg";
import continuousVirtualCare from "public/static/img/v5/reason/virtualCare/continuous.jpg";
import piggyBankIllustration from "public/static/img/v5/illustrations/piggy-bank.jpg";

type CarbonForCampusConstants = {
  faq: (QA | SectionDivider)[];
  imageItems: ImageItem[];
  benefits: {
    src: string;
    alt: string;
    header: string;
    list: string[];
    backgroundColor: string;
    pattern: string;
  }[];
  data: {
    header: string | JSX.Element;
    arrowStyle: React.CSSProperties;
    img: string;
    alt: string;
    list: {
      header: string;
      text: string;
    }[];
  }[];
  ctaProps: BtnProps;
  testimonials: Testimonial[];
};

export const useCarbonForCampusConstants = (): CarbonForCampusConstants => {
  const {t} = useTranslation();
  const faq = useFaqIndex([
    "How does partnering with Carbon Health healthcare lower costs?",
    "How do you ensure student satisfaction?",
    "Is Carbon Health HIPAA compliant?",
    "Is the Carbon Health app secure?",
    "What protections does Carbon have in place to protect my personal and health information from hackers or outsiders getting into the app or into their databases that store my information?",
  ]);

  const imageItems: ImageItem[] = [
    {
      header: t("Comprehensive care"),
      content: t(
        "We offer a modern, flexible solution that integrates primary, urgent, and mental healthcare with nationwide virtual access to providers 7 days a week, 52 weeks a year.",
      ),
      image: {
        src: doctorAndPatientIllustration,
        height: 125,
        alt: t("Illustration of a doctor and patient discussing the patient’s care."),
      },
    },
    {
      header: t("Flexible options"),
      content: t(
        "With our in-person locations, extended hours, and desktop and mobile apps for virtual care, your students have easy access to high-quality healthcare, whether they’re on campus or learning remotely from a distance. ",
      ),
      image: {
        src: continuousVirtualCare,
        height: 125,
        alt: t(
          "Illustration of a young person wearing a face mask and making the peace sign with their hand.",
        ),
      },
    },
    {
      header: t("Lower costs"),
      content: t(
        "Symptom management, continuous health monitoring, mental healthcare, and sexual health services can prevent emergencies and more-serious problems from developing down the line, lowering overall costs.",
      ),
      image: {
        src: piggyBankIllustration,
        height: 125,
        alt: t("Illustration of a hand dropping a coin into a piggy bank."),
      },
    },
  ];

  const benefits = [
    {
      src: "/illustrations/mobile-app",
      alt: t("Illustration of a cell phone"),
      header: t("Comprehensive health services"),
      list: [
        t("Primary & urgent care"),
        t("Mental health services"),
        t("Immunization management"),
        t("Women's health"),
        t("LGBTQ+ health"),
        t("Sexual health"),
      ],
      backgroundColor: colors.lightGreen,
      pattern: "0",
    },

    {
      src: "/illustrations/virtual-care",
      alt: t("Illustration of a laptop"),
      header: t("Increased access to healthcare"),
      list: [
        t("Year-round care"),
        t("Virtual visits & same-day appointments"),
        t("Instant access to results and treatment plans in the app"),
        t("Weekend and extended hours"),
      ],
      backgroundColor: colors.lightBlue,
      pattern: "1",
    },
    {
      src: "/illustrations/syringe-and-vial",
      alt: t("Illustration of a syringe and vial"),
      header: t("Immunization management"),
      list: [
        t("Digital immunization program"),
        t("Immunization status reports"),
        t("Vaccine schedule notifications"),
        t("Health Pass digital vaccine card"),
      ],
      backgroundColor: colors.lightYellow,
      pattern: "2",
    },
    {
      src: "/illustrations/doctor-and-patient",
      alt: t("Illustration of a doctor and patient"),
      header: t("Modern, high-quality locations"),
      list: [
        t("High-quality and professionally run locations"),
        t("Bright, airy spaces"),
        t("Net Promoter Score (NPS) of 89"),
        t("Holistic healthcare"),
        t("Evidence-based medicine"),
      ],
      backgroundColor: colors.lightRed,
      pattern: "3",
    },
    {
      src: "/illustrations/happy-healthy-person-smiling",
      alt: t("Illustration of a patient receiving virtual care"),
      header: t("Sexual health and wellness program"),
      list: [
        t("STI evaluation and tests"),
        t("Sexual health education"),
        t("Risk assessment & prevention"),
        t("Contraceptive options"),
        t("Same-day access for sexual health issues"),
      ],
      backgroundColor: colors.lightPurple,
      pattern: "4",
    },
    {
      src: "/illustrations/virus",
      alt: t("Illustration of a virus molecule"),
      header: t("Complete COVID-19 coverage"),
      list: [
        t("Testing"),
        t("Vaccine map"),
        t("Health Pass digital vaccine card"),
        t("COVID-Positive care program"),
        t("Medical consultations"),
        t("Enterprise dashboard with real-time reporting"),
      ],
      backgroundColor: colors.mint,
      pattern: "5",
    },
  ];

  const data = [
    {
      header: (
        <Trans>
          Return to <span className="darkerYellow">school safely</span>
        </Trans>
      ),
      arrowStyle: {
        left: "30%",
        transform: "rotate(75deg)",
      },
      img: "/illustrations/person-in-chair-with-dog",
      alt: t(
        "Illustration of a person sitting in a comfy chair with their laptop, while their dog lays happily beside them.",
      ),
      list: [
        {
          header: t("Digital vaccine tracking program"),
          text: t(
            "Our program registers students at the beginning of each term and collects their immunization info.",
          ),
        },
        {
          header: t("Real-time immunization status reports"),
          text: t(
            "We make status reports available to student affairs administrators for decision-making. Students receive vaccine and immunization notifications, so you don’t have to send out reminders.",
          ),
        },
        {
          header: t("Health Pass: digital COVID-19 vaccine card"),
          text: t(
            "Students can verify their vaccine status with our industry-leading, HIPAA-compliant digital vaccine card.",
          ),
        },
      ],
    },
    {
      header: (
        <Trans>
          Support for <span className="darkerYellow">mental health</span>
        </Trans>
      ),
      arrowStyle: {
        left: "35%",
        transform: "rotate(126deg)",
      },
      img: "/illustrations/person-in-virtual-care-session",
      alt: t(
        "Illustration of a person looking at their smart phone during a video call with their healthcare providers.",
      ),
      list: [
        {
          header: t("Comprehensive mental health services"),
          text: t(
            "Our services are accessible to all students regardless of location — services include individual therapy, group therapy, and environmental response.",
          ),
        },
        {
          header: t("Customized response programs"),
          text: t(
            "Carbon Health's clinical team will work with you to develop tailored programs in response to on-campus and global events.",
          ),
        },
        {
          header: t("Access to services from anywhere"),
          text: t(
            "Carbon Health's comprehensive mental health services are accessible to all students regardless of location.",
          ),
        },
      ],
    },
    {
      header: (
        <Trans>
          Care <span className="darkerYellow">when & where</span> students need it
        </Trans>
      ),
      arrowStyle: {
        left: "5%",
        top: -70,
        transform: "rotateX(180deg) rotate(-80deg)",
      },
      img: "/illustrations/person-on-bike-with-phone",
      alt: t("Illustration of a person standing with their bicycle, while looking at their phone."),
      list: [
        {
          header: t("Access from anywhere on any day"),
          text: t(
            "We offer nationwide access to coverage 7 days a week, 52 weeks a year. Your remote and distributed students can get care virtually, or walk into any of 73 Carbon Health locations in 10 states (soon to be 150 locations around the country).",
          ),
        },
        {
          header: t("Chat with a provider from any device"),
          text: t(
            "Our mobile apps are compatible with iOS and Android, and through the app you can chat with your provider, conduct virtual visits, and order prescription refills.",
          ),
        },
      ],
    },
  ];

  const ctaProps = {
    "aria-label": t("Click to scroll to the contact form."),
    href: "#get-started",
    children: t("Get Started"),
  };

  const testimonials: Testimonial[] = [
    {
      body: t(
        "Carbon Health’s virtual and in-person offerings make it easy for students to keep in touch with their healthcare provider, make appointments, and access their medical records through the app.",
      ),
      bg: colors.darkerYellow,
      color: "#fff",
      who: {
        name: "Grace Hum",
        who: t("Assistant Dean of Students at UC Law SF Law"),
      },
    },
  ];

  return {faq, imageItems, benefits, data, ctaProps, testimonials};
};
